import './app.css';
import '@holo/ui/css/reset.css';
import '@holo/ui/css/skeleton.css';

import { Suspense } from 'react';
import { ApolloProvider, setupApollo } from '@holo/client';
import { NextThemeProvider } from '@tamagui/next-theme';
import { appWithTranslation } from '@holo/i18n';
import DefaultLayout from '@/layouts/DefaultLayout';
import NProgress from 'nprogress';
import Router from 'next/router';
import dynamic from 'next/dynamic';
import { Inter } from 'next/font/google';
import type { AppProps } from 'next/app';
import type { NextPage } from 'next';
import type { ScriptProps } from 'next/script';
import { DesignProvider, config } from '@holo/ui/core';
import { Analytics } from '@vercel/analytics/react';

const Toaster = dynamic(() => import('sonner').then((mod) => mod.Toaster), { ssr: true });

// Page and Props is a way to extend Component with a Layout property
type Page<P = any> = NextPage<P> & {
  Layout: (page: ScriptProps) => JSX.Element;
};

type AppPropsWithLayout = AppProps & {
  Component: Page;
};

NProgress.configure({ showSpinner: false });

Router.events.on('routeChangeStart', () => NProgress.start());
Router.events.on('routeChangeError', () => NProgress.done());
Router.events.on('routeChangeComplete', () => {
  NProgress.done();
});

const inter = Inter({
  subsets: ['latin'],
});

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
  const client = setupApollo(pageProps);

  const Layout = Component.Layout || DefaultLayout;

  return (
    <ApolloProvider client={client}>
      {/* NOTE: drawers and modals needs this for some reason otherwise we rely on variable fonts */}
      {/* eslint-disable-next-line */}
      <style jsx global>{`
          :root {
            --font-inter: ${inter.style.fontFamily};
          }
        `}</style>
      <NextThemeProvider forcedTheme='light' skipNextHead>
        <DesignProvider config={config} disableInjectCSS>
          <Layout>
            <Suspense fallback={null}>
              <Toaster position="bottom-right" />
              <Component {...pageProps} />
              <Analytics debug={false} />
            </Suspense>
          </Layout>
        </DesignProvider>
      </NextThemeProvider>
    </ApolloProvider>
  );
};

export default appWithTranslation(App);
